// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@flip/App/Components/Notify";
import BrandAlphabetNavigation, {
    BrandAlphabetNavigationStyles,
} from "@flip/Overrides/Widgets/Brand/BrandAlphabetNavigation";
import BackTop from "@flip/XDL/Icons/BackTop";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, ellipsis, xScreen } from "@flip/XDL/Utilities";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { VerticalColumnCellProps } from "@insite/content-library/Components/VerticalColumnCell";
import { VerticalColumnContainerProps } from "@insite/content-library/Components/VerticalColumnContainer";
import { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type BrandLetterMap = {
    [letter: string]: BrandModel[];
};

export interface BrandListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    brandAlphabetNavigation?: BrandAlphabetNavigationStyles;
    brandAccordion?: AccordionProps;
    brandAccordionSection?: AccordionSectionPresentationProps;
    verticalColumnContainer?: VerticalColumnContainerProps;
    verticalColumnCell?: VerticalColumnCellProps;
    brandLink?: LinkPresentationProps;
    actions?: InjectableCss;
    actionLinks?: LinkPresentationProps;
    actionLinksDisabled?: TypographyPresentationProps;
    buttons?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
    searchKeyTitleWrapper?: InjectableCss;
    brandSections?: InjectableCss;
    brandAlphabetSections?: InjectableCss;
    brandItemsWrapper?: InjectableCss;
    allBrandsTitle?: TypographyPresentationProps;
    clearFiltersButton?: ButtonPresentationProps;
    brandLetter?: TypographyPresentationProps;
    backToTopLink?: LinkPresentationProps;
}

export const listStyles: BrandListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    brandLetter: {
        variant: "h3",
        css: css`
            text-transform: uppercase;
        `,
    },
    backToTopLink: {
        typographyProps: {
            size: 14,
            weight: fontConfig.weight.semiBold,
            css: css`
                line-height: 22px;
                letter-spacing: 0;
                margin-bottom: 16px;
            `,
        },
        icon: {
            iconProps: {
                src: BackTop,
                size: 10,
            },
        },
        css: css`
            align-items: baseline;
        `,
    },
    // XNG-Chanegs: FLIP-507
    brandLink: {
        typographyProps: {
            variant: "p",
            weight: 500,
            css: css`
                ${ellipsis(2)};
                margin-bottom: 0;
            `,
        },
        css: css`
            ${({ isAuthorized }: any) =>
                !isAuthorized &&
                css`
                    &&& {
                        ${TypographyStyle} {
                            color: ${color("text")};
                            &:visited {
                                color: ${color("text")};
                            }
                            &:hover {
                                color: ${color("secondary")};
                            }
                        }
                    }
                `}
        `,
    },
    brandSections: {
        css: css`
            display: flex;
            flex-direction: column;
            &:not(.first-item) {
                margin-top: 24px;

                ${xScreen("lte", 1024)} {
                    margin-top: 48px;
                }
            }
        `,
    },
    brandAlphabetSections: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
        `,
    },
    brandItemsWrapper: {
        css: css`
            border-top: 1px solid ${color("common.ui4")};
            padding-top: 26px;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-bottom: 24px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    searchKeyTitleWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: wrap;
            ${xScreen("lte", "sm")} {
                margin-bottom: 12px;
            }
        `,
    },
};

const styles = listStyles;

interface BrandListState {
    expanded: boolean | "mixed";
    brandLetterDetails: BrandAlphabetLetterModel[];
    brandSections: BrandSection[];
    // XNG-Changes
    selectedLetter: string;
}

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const BrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState } = props;

    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = brandAlphabetState.value || [];
    const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    // XNG-Changes
    const [searchFilterBrands, setSearchFilterBrands] = React.useState("");
    const { brandLetterDetails, brandSections } = buildBrandSections(
        letters,
        brandLettersMap,
        brandAlphabet,
        false,
        searchFilterBrands,
    );

    const [state, setState] = React.useState<BrandListState>({
        expanded: false,
        brandLetterDetails,
        brandSections,
        selectedLetter: "",
    });

    // XNG-Change:
    React.useEffect(() => {
        setState({ ...state, brandLetterDetails, brandSections });
    }, [brandLetterDetails?.length, brandSections?.length, searchFilterBrands]);

    if (allBrandsDataView.isLoading || brandAlphabetState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    if (brandList.length === 0) {
        return null;
    }

    const onClickBrandSection = (section: BrandSection) => () => {
        setState(({ brandSections, expanded, brandLetterDetails, selectedLetter }: BrandListState) => {
            const itemIndex = brandSections.findIndex(item => item.letter === section.letter);
            const nextSectionExpanded = !section.expanded;
            let nextExpanded = expanded;
            if (expanded !== "mixed" && expanded !== nextSectionExpanded) {
                nextExpanded = "mixed";
            } else if (expanded === "mixed") {
                const hasNonMatchingExpanded = brandSections.find(item => {
                    return item.letter !== section.letter && item.expanded !== nextSectionExpanded;
                });
                if (!hasNonMatchingExpanded) {
                    nextExpanded = nextSectionExpanded;
                }
            }
            const newSectionState = { ...section, expanded: nextSectionExpanded };
            const newBrandSections = [...brandSections];
            newBrandSections.splice(itemIndex, 1, newSectionState);
            return {
                brandSections: newBrandSections,
                expanded: nextExpanded,
                brandLetterDetails,
                selectedLetter: section.letter,
            };
        });
    };

    // const onCollapse = (event: React.MouseEvent) => {
    //     event.stopPropagation();
    //     if (state.expanded === "mixed" || state.expanded) {
    //         const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    //         const { brandLetterDetails, brandSections } = buildBrandSections(
    //             letters,
    //             brandLettersMap,
    //             brandAlphabet,
    //             false,
    //         );
    //         setState({ ...state, brandLetterDetails, brandSections, expanded: false });
    //     }
    // };

    // const onExpand = (event: React.MouseEvent) => {
    //     event.stopPropagation();
    //     if (state.expanded === "mixed" || !state.expanded) {
    //         const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    //         const { brandLetterDetails, brandSections } = buildBrandSections(
    //             letters,
    //             brandLettersMap,
    //             brandAlphabet,
    //             true,
    //         );
    //         setState({ ...state, brandLetterDetails, brandSections, expanded: true });
    //     }
    // };

    const handleBackToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        const elementToFocus = document.getElementById("collapseButton");
        if (elementToFocus) {
            elementToFocus.focus();
        } else {
            document.getElementById("expandButton")?.focus();
        }
    };

    // XNG-Changes
    const clearFilters = () => {
        setState(prevState => ({ ...prevState, selectedLetter: "" }));
        setSearchFilterBrands("");
    };

    const handleBrandLetterClick = (letter: string) => {
        if (letter === "all") {
            clearFilters();
        }
        const section = state.brandSections.find(item => item.letter === letter);
        section && onClickBrandSection(section)();
    };

    // XNG-Changes: adding filter's function
    const handleSearchFilterBrands = (searchFilterTextValue: string) => {
        setSearchFilterBrands(searchFilterTextValue);
    };

    // XNG-Changes
    const getTitle = () => {
        let title = translate("All Brands");
        if (state.selectedLetter) {
            title = translate(`Filtered Results for: "${state.selectedLetter?.toUpperCase()}"`);
        }
        if (searchFilterBrands) {
            title = translate(`Filtered Results for: "${searchFilterBrands}"`);
        }
        return title;
    };

    const brandSectionsState = state.selectedLetter
        ? state.brandSections.filter(brand => brand.letter === state.selectedLetter)
        : state.brandSections;

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandList">
            <BrandAlphabetNavigation
                letterDetails={brandLetterDetails}
                extendedStyles={styles.brandAlphabetNavigation}
                onBrandLetterClick={handleBrandLetterClick}
                handleSearchFilterBrands={handleSearchFilterBrands}
                searchFilterBrands={searchFilterBrands}
                selectedLetter={state.selectedLetter}
            />
            {/* XNG-Change: */}
            {(state.selectedLetter || searchFilterBrands) && (
                <StyledWrapper {...styles.titleWrapper}>
                    <Button {...styles.clearFiltersButton} onClick={clearFilters} aria-label="Clear Filters">
                        {translate("Clear Filters")}
                    </Button>
                </StyledWrapper>
            )}
            {brandSectionsState.length > 0 &&
                brandSectionsState.filter(brandDetails => brandDetails.brandMap.length > 0).length === 0 && (
                    <Notify type="warning">{translate("No result  found")}</Notify>
                )}
            {brandSectionsState
                .filter(brandDetails => brandDetails.brandMap.length > 0)
                .map((brandDetails, index) => (
                    <StyledWrapper
                        key={brandDetails.letter}
                        className={index === 0 ? "first-item" : ""}
                        {...styles.brandSections}
                    >
                        <StyledWrapper {...styles.brandAlphabetSections}>
                            <Typography
                                id={`letter-${brandDetails.letter === "#" ? "number" : brandDetails.letter}`}
                                {...styles.brandLetter}
                            >
                                {brandDetails.letter}
                            </Typography>
                            <Link onClick={() => handleBackToTop()} {...styles.backToTopLink}>
                                {translate("Back to Top")}
                            </Link>
                        </StyledWrapper>
                        {/* XNG-Changes: FLIP-507  */}
                        <GridContainer {...styles.brandItemsWrapper} gap={16}>
                            {brandDetails.brandMap.map(brand => (
                                <GridItem key={brand.detailPagePath} width={[12, 6, 4, 3, 3]}>
                                    <Link
                                        {...styles.brandLink}
                                        href={brand.detailPagePath}
                                        data-test-selector="brandLink"
                                        aria-label={brand.name}
                                        {...{ isAuthorized: brand.properties.authorizedSupplier }}
                                    >
                                        {brand.name}
                                    </Link>
                                </GridItem>
                            ))}
                        </GridContainer>
                    </StyledWrapper>
                ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        allowedContexts: ["BrandsPage"],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
    expanded: boolean;
}

/**
 * Takes in a brand list and sorts it into #, A-Z, Other characters.
 *
 * @param brandList The list of brands that should be sorted.
 */
const buildLettersAndMap = (brandList: BrandModel[]): { letters: string[]; brandLettersMap: BrandLetterMap } => {
    let letters: string[] = [];
    let letter: string;
    let newLetters: string[] = letters;
    const brandLettersMap: BrandLetterMap = {};
    for (let i = 0; i < brandList.length; i = i + 1) {
        const brand = brandList[i];
        letter = brand.name[0] ? brand.name[0].toLowerCase() : "";
        if (isNumeric(letter)) {
            letter = "#";
        }
        if (!brandLettersMap[letter]) {
            brandLettersMap[letter] = [];
        }
        brandLettersMap[letter].push(brand);
        newLetters = [...newLetters, letter];
    }
    letters = newLetters.filter((letter, index, array) => array.indexOf(letter) === index);
    if (brandLettersMap["#"]) {
        brandLettersMap["#"].sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { letters, brandLettersMap };
};

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param letters List of letters and special characters.
 * @param brandLettersMap Brand list sorted into letter lists.
 * @param brandAlphabet Letter count list.
 * @param expanded The expanded value to use if not undefined.
 * XNG-change
 * @param searchFilterBrands
 */
const buildBrandSections = (
    letters: string[],
    brandLettersMap: BrandLetterMap,
    brandAlphabet: BrandAlphabetLetterModel[],
    expanded: boolean | undefined,
    searchFilterBrands?: string,
): {
    brandLetterDetails: {
        count: number;
        letter: string;
        linkable: boolean;
    }[];
    brandSections: {
        letter: string;
        brandMap: BrandModel[];
        expanded: boolean;
    }[];
} => {
    const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
        ...alphabetItem,
        linkable: alphabetItem.count > 0,
    }));

    // XNG-Changes
    const brandSections = letters.map(letter => ({
        letter,
        brandMap: getBrandLettersMap(brandLettersMap, letter, searchFilterBrands),
        expanded: !!expanded,
    }));

    return { brandLetterDetails, brandSections };
};

// XNG change
const getBrandLettersMap = (
    brandLettersMap: BrandLetterMap,
    letter: string,
    searchFilterBrands?: string | undefined,
) => {
    if (!searchFilterBrands) {
        return brandLettersMap[letter];
    }

    brandLettersMap[letter] = brandLettersMap[letter].filter(brand =>
        (brand.name || "").toLowerCase().includes(searchFilterBrands.toLowerCase()),
    );

    return brandLettersMap[letter];
};
